import { Box, Flex, Text } from "@chakra-ui/react";
import {
  BaseModal,
  Button,
  getTrackingId,
  MessageBox,
  PinCodeInput,
  type ModalRef
} from "@euka-mfes/bark";
import { LearningClassEnums as CLS } from "@/ts/enums/LearningClassEnums";
import { type Ref } from "react";
import { Trans } from "react-i18next";

interface PinInputModalProps {
  modalRef: Ref<ModalRef>;
  pinCodes: string[];
  onPinVerification: () => void;
  handleRequestNewCode: () => void;
  errorMessage?: string;
  isRequestNewCode?: boolean;
  handleInputChange: (value: string[]) => void;
  isSubmitOTP?: boolean;
  handlePinCancel: () => void;
  title: string;
  descriptionLineOne: string;
  descriptionLineTwo: string;
  isProgrammaticCloseRef?: React.MutableRefObject<boolean>;
}

const PinInputModal = ({
  modalRef,
  pinCodes,
  onPinVerification,
  handleRequestNewCode,
  errorMessage,
  isRequestNewCode,
  handleInputChange,
  isSubmitOTP,
  handlePinCancel,
  title,
  descriptionLineOne,
  descriptionLineTwo,
  isProgrammaticCloseRef
}: PinInputModalProps) => {
  const getErrors = () => {
    if (errorMessage !== "") {
      return <MessageBox type="error">{errorMessage}</MessageBox>;
    }
  };

  const handleClose = () => {
    handlePinCancel();
    (modalRef as React.RefObject<ModalRef>)?.current?.dismiss?.();
  };

  return (
    <BaseModal
      ref={modalRef}
      title={<Trans>{title}</Trans>}
      onPrimaryBtnClick={onPinVerification}
      primaryBtnTxt={<Trans>Continue</Trans>}
      secondaryBtnTxt={<Trans>Cancel</Trans>}
      size="lg"
      infoBanners={getErrors()}
      isLoading={isRequestNewCode}
      isPrimaryBtnLoading={isSubmitOTP}
      onSecondaryBtnClick={handleClose}
      resetModalStateOnClose={() => {
        if (!isProgrammaticCloseRef?.current) {
          handlePinCancel();
        }
        if (isProgrammaticCloseRef) isProgrammaticCloseRef.current = false;
      }}
      loaderStyles={{ loadingText: "Sending new code" }}
    >
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        p="48px"
        bg="canvas"
        data-testid="pin-code-modal"
        data-tracking-id={getTrackingId(
          [CLS.PinInputModal, CLS.Modal],
          "pin-code"
        )}
      >
        <Flex gap={2} flexDir="column">
          <Text textStyle="body.large" textAlign="justify">
            {descriptionLineOne}
          </Text>
          <Text textStyle="body.large" textAlign="justify">
            {descriptionLineTwo}
          </Text>
        </Flex>
        <Flex p="32px 0px" alignSelf="center" flexDir="column">
          <PinCodeInput
            value={pinCodes}
            handleInputChange={handleInputChange}
            data-tracking-id={getTrackingId(
              [CLS.PinInputModal, CLS.Input],
              "pin-code"
            )}
          />
        </Flex>
        <Flex gap={2} flexDir="column">
          <Text
            textStyle="body.standard"
            fontWeight="semibold"
            textAlign="left"
          >
            <Trans>Didn’t receive the code?</Trans>
          </Text>
          <Text textStyle="body.standard" textAlign="left">
            <Trans>
              Remind your parent to check their junk/spam folder or{" "}
              <Button
                variant="textLink"
                onClick={handleRequestNewCode}
                verticalAlign="baseline"
                data-tracking-id={getTrackingId(
                  [CLS.PinInputModal, CLS.Button],
                  "request-new-code"
                )}
              >
                request new code
              </Button>
              .
            </Trans>
          </Text>
        </Flex>
      </Box>
    </BaseModal>
  );
};

export default PinInputModal;
